// ⚠️ FICHIER ENGENDRÉ — NE PAS ÉDITER À LA MAIN.
// Source : lib/operateur-leviers.js + lib/leviers/*.js
// Régénérer : node scripts/generer-miroir-leviers.js
// Pourquoi ce doublon : CRA/webpack refuse les imports hors de src/ (Jest, lui,
// les accepte — d'où le risque de tests verts sur un build cassé).
// lib/leviers/messages.js — LES LEVIERS DE LA PAROLE DE FBZ.
//
// La famille « messages » : ce qui règle CE QUE FBZ dit à ses voyageurs, et par
// quel canal. Neuf leviers déclarés le 2026-09-09, sur les seize actions du
// périmètre. Les sept absents sont justifiés en bas de fichier — une exclusion
// sans son motif finit par être levée par quelqu'un qui la croit excessive.
//
// ══ LA LIGNE DE PARTAGE DE CETTE FAMILLE ════════════════════════════════════
//
// Aucun de ces leviers n'envoie quoi que ce soit AU MOMENT DU CLIC. Mais deux
// natures cohabitent, et elles ne se valent pas :
//
//   • le CONTENU (gabarits, fragments, FAQ, consignes du copilote) — modifier
//     un texte ne fait partir aucun message ; il PARTIRA au prochain envoi, tel
//     qu'il vient d'être écrit. Chaque `constatable` le dit, parce que c'est
//     exactement ce qu'on ne voit pas à l'écran juste après le clic ;
//   • le DÉCLENCHEUR (heure, délai, statut, ciblage) — là on ne change pas les
//     mots, on change QUI reçoit et QUAND. D'où le marquage `sortant` sur les
//     deux leviers concernés, et la vague 9 pour le plus lourd d'entre eux.
//
// ══ ET LE TEXTE, C'EST DIMITRI QUI L'ÉCRIT ══════════════════════════════════
//
// Plusieurs de ces leviers portent un champ de texte destiné à un voyageur. Le
// socle est net : l'opérateur ne CHOISIT jamais « un texte destiné à un tiers ».
// Ce n'est pas une contradiction — la carte porte le champ, Dimitri le remplit,
// et c'est ce que la vague 9 nomme (« demande un texte que Dimitri écrit — en
// dernier, jamais exclu »). Un levier qui ne fait que déplacer un texte déjà
// écrit, lui, reste en vague 3.

// ══ « vise=logement » AU CATALOGUE — LE NOM DIT, PAS L'IDENTIFIANT ═════════
//
// Les leviers de cette famille qui portent `args_resolus` ne montrent plus
// `logement_id` au modèle : il pose « Cocon Naturel » dans `vise`, et le
// serveur retrouve `lc1` au moment de composer la carte
// (`lib/reperes-geste.js`). L'argument RESTE dans `args_autorises` — l'en
// retirer ferait refuser la carte au clic (`filtrerArgs`, rejoué par le
// navigateur) et casserait les aperçus qui le citent en `depend_de`.
//
// ⚠️ ET SI LE MODÈLE LE COMPOSE QUAND MÊME, LA CARTE NE VIENT PAS. On ne lui
// demande pas de s'abstenir : on rend la chose sans effet, et l'écran demande
// à Dimitri de nommer le logement. « Cocon » tout seul désigne DEUX logements
// aux commissions différentes — ce n'est pas un détail de confort.
const LEVIERS_MESSAGES = Object.freeze({
  // ══ LE COPILOTE : SA CONNAISSANCE ET SON ÉLIGIBILITÉ ═════════════════════
  // Ces quatre-là vivent dans la FICHE LOGEMENT (onglet Savoir / Copilote IA,
  // src/tabs/logements/LogementIATab.jsx + src/views/logements/FicheSavoirCtrl.jsx)
  // — d'où `ecran: "logements"` et non "messages" : Dimitri constate là où
  // l'écran est, pas là où la famille est rangée.

  // ⚠️ `inbox_ia_config_save` A ÉTÉ RETIRÉ LE 2026-09-09 — il est désormais un INTERDIT ABSOLU
  // (`lib/operateur-leviers.js`), sur décision de Dimitri en préparant la
  // vague 4. La raison voyage avec la règle, là-bas : une interdiction sans son
  // motif finit par être levée par quelqu'un qui la croit prudente à l'excès.

  inbox_ia_instructions_save: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "inbox_ia_instructions_save",
    // handleInboxIaInstructionsSave : upsert livret_instructions_ia {content}.
    // Le contenu REMPLACE l'existant — pas d'historique en base.
    args_autorises: ["logement_id", "content"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    titre: "Réécrire les consignes du copilote",
    constatable:
      "les consignes affichées dans la fiche du logement deviennent le texte validé ; le copilote les suivra dès son prochain brouillon",
    // Contenu éditorial : ce sont des consignes INTERNES (le copilote les lit,
    // le voyageur ne les voit jamais) — vague 3 et pas 9.
    sujet: "messages",
    onglet: "savoir",
    cout: ["rien"],
    // Des consignes INTERNES : le copilote les lit, le voyageur ne les voit
    // jamais. Ce qui en sortira ensuite est un brouillon, pas ce texte.
    ou_ca_part: ["personne"],
    sans_retour:
      "le texte remplacé n'est gardé nulle part : il n'y a aucun historique de version sur les consignes d'un logement",
    ne_fait_pas:
      "il n'envoie rien et n'allume rien : ni l'éligibilité du logement au copilote, ni l'envoi réel. Les brouillons déjà écrits gardent leur ancien ton.",
    apercu_absent:
      "aucune lecture ne rend les consignes actuelles : il faudrait `inbox_ia_instructions_lire`. Le geste REMPLACE un texte que la carte ne montre pas — c'est le manque le plus net de cette famille.",
    dit_comme: [
      "Change les consignes que suit l'assistant sur ce logement",
      "Dis à l'assistant comment répondre au Cocon",
      "Réécris les instructions du copilote pour ce logement",
    ],
    vague: 3,
    motif_requis: false,
    autonome: false,
  },

  inbox_ia_faq_create: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "inbox_ia_faq_create",
    // handleInboxIaFaqCreate : insert livret_faq_apprises {question, answer,
    // source_message_id}. Question ET réponse sont SCRUBBÉES gate-fermé avant
    // insert (une FAQ est réutilisée pour tous les voyageurs : jamais un code
    // de porte ni une adresse en base).
    args_autorises: ["logement_id", "question", "answer", "source_message_id"],
    titre: "Apprendre une question au copilote",
    constatable:
      "la question et sa réponse apparaissent dans la liste des FAQ apprises de la fiche du logement",
    // Le texte de la réponse est celui que Dimitri écrit — et il sera resservi
    // à des voyageurs. Dernière vague, comme le socle le demande.
    sujet: "messages",
    onglet: "savoir",
    cout: ["rien"],
    // La réponse sera resservie à des voyageurs, mais par un brouillon que
    // Dimitri relit : rien ne part du fait de ce geste.
    ou_ca_part: ["personne"],
    sans_retour: null, // on AJOUTE ; la FAQ s'archive ensuite (`_update`)
    ne_fait_pas:
      "il n'envoie rien : la réponse entre dans ce que le copilote sait, elle ne part chez personne. Et le serveur la SCRUBBE — un code de porte ou une adresse n'y entrera pas.",
    apercu: {
      lecture: "inbox_ia_faq_lire",
      depend_de: ["logement_id"],
      // ⚠️ `x-apercu-version`, PAS `x-cfg-version` — ce geste n'écrit pas le
      // blob de configuration. Le jeton prouve QUE LA CARTE A LU (executerGeste
      // refuse de partir sans lui) ; l'écriture, elle, ne le vérifie pas encore
      // (lot à part, avec l'écran — cf. lib/apercus-messages-handlers.js).
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Retiens cette question et cette réponse pour la prochaine fois",
      "Apprends ça à l'assistant",
      "Ajoute cette question dans ce qu'il sait",
    ],
    vague: 9,
    motif_requis: false,
    autonome: false,
  },

  inbox_ia_faq_update: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "inbox_ia_faq_update",
    // handleInboxIaFaqUpdate : patch partiel {question?, answer?, status?} sur
    // une FAQ, double cloisonnement id + logement_id. `status` n'accepte que
    // 'active' ou 'archived'.
    // ⚠️ C'EST LE CHEMIN RÉVERSIBLE DE LA SUPPRESSION : archiver une FAQ la
    // retire du service SANS l'effacer. `inbox_ia_faq_delete` (DELETE dur) est
    // écarté pour cette raison exacte — voir la note de fin.
    args_autorises: ["id", "logement_id", "question", "answer", "status"],
    // ⚠️ Valeurs RECOPIÉES de la liste fermée du serveur — sans elles, le
    // modèle compose du plausible et la carte échoue sous le clic.
    args_valeurs: {
      status: ["active", "archived"],
    },
    titre: "Corriger ou archiver une question apprise",
    constatable:
      "la FAQ change de texte, ou passe en « archivée », dans la liste de la fiche du logement",
    sujet: "messages",
    onglet: "savoir",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour:
      "corriger écrase le texte d'avant, qui n'est gardé nulle part ; archiver, en revanche, se défait (status: active)",
    ne_fait_pas:
      "archiver ne SUPPRIME pas : la FAQ reste en base, elle cesse seulement d'être servie. La suppression dure, elle, n'est pas un geste de l'opérateur (E1).",
    apercu: {
      lecture: "inbox_ia_faq_lire",
      depend_de: ["id", "logement_id"],
      // ⚠️ `x-apercu-version`, PAS `x-cfg-version` — ce geste n'écrit pas le
      // blob de configuration. Le jeton prouve QUE LA CARTE A LU (executerGeste
      // refuse de partir sans lui) ; l'écriture, elle, ne le vérifie pas encore
      // (lot à part, avec l'écran — cf. lib/apercus-messages-handlers.js).
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Corrige cette réponse apprise",
      "Archive cette question, elle n'est plus vraie",
      "Change ce que l'assistant répond là-dessus",
    ],
    vague: 9,
    motif_requis: false,
    autonome: false,
  },

  // ══ LE PARCOURS DIRECT : LES GABARITS ════════════════════════════════════

  direct_messages_save: {
    ecran: "messages",
    endpoint: "/api/beds24-mapping",
    action: "direct_messages_save",
    // handleDirectMessagesSave, op 'set' : update direct_messages {subject_*,
    // body_*, is_edited:true, name si manuel}. Objet ET corps FR exigés (le
    // contenu base REMPLACE entièrement le défaut). EN/ES tout-ou-rien, HTML
    // actif refusé, variables validées contre le catalogue du slug.
    //
    // ⚠️ `op` N'EST PAS DANS LA LISTE, DÉLIBÉRÉMENT. Le handler retombe alors
    // sur 'set'. `op:'reset'` remettrait tous les champs à NULL — c'est-à-dire
    // effacerait sans retour un texte que Dimitri a écrit, pendant que la carte
    // annoncerait « revenir au défaut ». Le bouton existe dans l'UI, où le
    // texte perdu est sous les yeux ; il n'a pas à passer par une intention.
    args_autorises: [
      "slug",
      "subject_fr",
      "body_fr",
      "subject_en",
      "body_en",
      "subject_es",
      "body_es",
      "name",
    ],
    // ⚠️ LE SLUG NE SE PRONONCE PAS — ET LE MODÈLE NE L'A JAMAIS VU. Dimitri
    // dit « le message de demande reçue », jamais `demande_recue_voyageur` :
    // un slug composé ici est forcément inventé, et il aurait fait réécrire un
    // AUTRE message que celui qu'il avait en tête. Il pose donc le nom dans
    // `vise`, et `lib/gabarit-repere.js` va chercher la ligne avec son jeton.
    // L'argument RESTE dans `args_autorises` (sinon `filtrerArgs` refuse la
    // carte au clic, et l'aperçu ne pourrait plus le citer).
    args_resolus: {
      slug: { objet: "gabarit_direct", par: ["nom"] },
    },
    titre: "Réécrire un message du parcours Direct",
    constatable:
      "le gabarit affiche le nouveau texte, marqué « modifié », dans Messages › Direct — et c'est CE texte qui partira au voyageur au prochain envoi de ce message",
    // Contenu éditorial pur : le message existe déjà, son déclencheur ne bouge
    // pas, seuls les mots changent.
    sujet: "messages",
    cout: ["rien"],
    // Rien ne part MAINTENANT — mais ces mots-là partiront, et l'e-mail du
    // parcours Direct est transactionnel : il n'est PAS gaté par
    // `MESSAGES_DRY_RUN` (ADR-272).
    ou_ca_part: ["atteignable"],
    sans_retour:
      "le texte remplacé n'est gardé nulle part : il n'y a pas d'historique de version sur un gabarit du parcours Direct",
    ne_fait_pas:
      "il n'envoie rien tout de suite et ne change pas le déclencheur du message : seuls les mots changent, et ils partiront au prochain déclenchement. Il ne remet pas non plus le texte d'origine — ce bouton-là n'est pas un geste de l'opérateur.",
    apercu: {
      lecture: "direct_message_lire",
      depend_de: ["slug"],
      // ⚠️ `x-apercu-version`, PAS `x-cfg-version` — ce geste n'écrit pas le
      // blob de configuration. Le jeton prouve QUE LA CARTE A LU (executerGeste
      // refuse de partir sans lui) ; l'écriture, elle, ne le vérifie pas encore
      // (lot à part, avec l'écran — cf. lib/apercus-messages-handlers.js).
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Réécris le mail de bienvenue des réservations directes",
      "Change le texte de ce message du parcours direct",
      "Ce mail est mal tourné, corrige-le",
    ],
    vague: 3,
    motif_requis: false,
    // Le geste n'envoie rien, mais il décide des mots d'un email transactionnel
    // NON gaté DRY_RUN : le prochain déclenchement les emportera vraiment.
    marquages: ["sortant"],
    autonome: false,
  },

  direct_messages_create: {
    ecran: "messages",
    endpoint: "/api/beds24-mapping",
    action: "direct_messages_create",
    // handleDirectMessagesCreate : insert direct_messages, slug généré SERVEUR
    // (manuel_<nom>_<hex> — jamais fourni par le client), trigger_kind
    // 'manuel', status 'brouillon', contenu NULL, canal ['email'].
    // La catégorie doit exister : on n'en crée jamais.
    args_autorises: ["category_id", "name"],
    // ⚠️ LA CATÉGORIE SE DIT, SON IDENTIFIANT NON. Dimitri dit « dans les
    // rappels » ou « dans Annulation » ; `category_id` est un UUID que rien ne
    // rend. Le résolveur reconnaît le slug ET le libellé — une catégorie n'a
    // pas deux identités.
    args_resolus: {
      category_id: { objet: "categorie_direct", par: ["nom"] },
    },
    titre: "Créer un message dans le parcours Direct",
    constatable:
      "un message vide en « brouillon » apparaît en fin de sa catégorie, dans Messages › Direct",
    // Rien ne part : un manuel naît sans texte et sans déclencheur. C'est de la
    // structure éditoriale, pas un envoi.
    sujet: "messages",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour: null, // un brouillon vide ; il se repasse en brouillon, jamais armé
    ne_fait_pas:
      "il ne crée AUCUN texte et aucun déclencheur : le message naît vide, en brouillon, et ne partira jamais tant que personne ne l'écrit puis ne l'active dans l'écran",
    apercu: {
      lecture: "direct_messages_lire",
      depend_de: ["category_id"],
      // ⚠️ `x-apercu-version`, PAS `x-cfg-version` — ce geste n'écrit pas le
      // blob de configuration. Le jeton prouve QUE LA CARTE A LU (executerGeste
      // refuse de partir sans lui) ; l'écriture, elle, ne le vérifie pas encore
      // (lot à part, avec l'écran — cf. lib/apercus-messages-handlers.js).
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Crée un nouveau message dans le parcours direct",
      "Ajoute un message que j'enverrai à la main",
      "Il me faut un message de plus dans cette catégorie",
    ],
    vague: 3,
    motif_requis: false,
    autonome: false,
  },

  // ══ RÉORDONNER — FERMÉ LE 2026-09-22 (D3) ════════════════════════════════
  //
  // `direct_messages_reorder` A ÉTÉ DÉCLARÉ ICI, EN VAGUE 2, ET IL EST
  // MAINTENANT ÉCARTÉ (`hors_atteinte`, lib/operateur-leviers.js). Son
  // argument est `ordered_slugs` : la liste ENTIÈRE des messages d'une
  // catégorie, dans l'ordre voulu. Dimitri dit « fais remonter ce message »,
  // jamais huit slugs à la suite — et le lot 2a, qui apprend à désigner un
  // gabarit PAR SON NOM, ne produit qu'un objet, pas un classement. Même
  // motif que `logement_gallery_set_cover` : un geste qui ne peut jamais
  // s'armer occupe une ligne du catalogue et se fait proposer pour rien.
  //
  // ⚠️ CE QUI LE RAMÈNERA : un repère de DÉPLACEMENT RELATIF (« mets-le avant
  // celui-ci »), qui est un lot à part. Pas avant.

  direct_messages_settings: {
    ecran: "messages",
    endpoint: "/api/beds24-mapping",
    action: "direct_messages_settings",
    // handleDirectMessagesSettings : patch {status, canal, trigger_condition
    // .hour / .offset_days / .feedback_threshold}. Bornes SERVEUR : status ∈
    // {actif, brouillon} · canal ⊆ {email, sms} jamais vide · un message `sys`
    // ne peut pas perdre le canal email · hour = HH:MM sur les `temp` seuls ·
    // offset_days = entier 0..7 sur `depart_voyageur` SEUL · feedback_threshold
    // = entier 1..5 sur `depart_voyageur` seul.
    args_autorises: [
      "slug",
      "status",
      "canal",
      "hour",
      "offset_days",
      "feedback_threshold",
    ],
    // ⚠️ LE SLUG NE SE PRONONCE PAS — ET LE MODÈLE NE L'A JAMAIS VU. Dimitri
    // dit « le message de demande reçue », jamais `demande_recue_voyageur` :
    // un slug composé ici est forcément inventé, et il aurait fait réécrire un
    // AUTRE message que celui qu'il avait en tête. Il pose donc le nom dans
    // `vise`, et `lib/gabarit-repere.js` va chercher la ligne avec son jeton.
    // L'argument RESTE dans `args_autorises` (sinon `filtrerArgs` refuse la
    // carte au clic, et l'aperçu ne pourrait plus le citer).
    args_resolus: {
      slug: { objet: "gabarit_direct", par: ["nom"] },
    },
    // ⚠️ Valeurs RECOPIÉES de la liste fermée du serveur — sans elles, le
    // modèle compose du plausible et la carte échoue sous le clic.
    args_valeurs: {
      status: ["actif", "brouillon"],
    },
    titre: "Régler l'heure ou le statut d'un message Direct",
    constatable:
      "le message affiche son nouveau réglage dans Messages › Direct (heure, délai de départ, statut, canaux) — c'est à cette heure-là qu'il partira au voyageur",
    // Réglages bornés des deux côtés par le serveur : le pire cas est un envoi
    // à la mauvaise heure, jamais un envoi impossible à défaire.
    sujet: "messages",
    cout: ["rien"],
    ou_ca_part: ["atteignable"],
    sans_retour: null, // des réglages bornés, reposables à l'identique
    // ⚠️ La confusion probable : croire que « brouillon » rattrape un envoi.
    ne_fait_pas:
      "il ne rattrape aucun message déjà parti et n'en envoie aucun : il décide de l'heure et du délai des PROCHAINS. Aujourd'hui, seuls l'heure et le délai comptent vraiment — les deux autres réglages ne sont lus par personne.",
    apercu: {
      lecture: "direct_message_lire",
      depend_de: ["slug"],
      // ⚠️ `x-apercu-version`, PAS `x-cfg-version` — ce geste n'écrit pas le
      // blob de configuration. Le jeton prouve QUE LA CARTE A LU (executerGeste
      // refuse de partir sans lui) ; l'écriture, elle, ne le vérifie pas encore
      // (lot à part, avec l'écran — cf. lib/apercus-messages-handlers.js).
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Envoie ce message à 10 h plutôt qu'à 8 h",
      "Décale ce mail à deux jours après le départ",
      "Change l'heure d'envoi de ce message",
    ],
    vague: 4,
    motif_requis: false,
    // ⚠️ `hour` et `offset_days` sont LUS PAR LE SWEEP quotidien : ce levier
    // change réellement le moment où un email atteint un voyageur. `status` et
    // `canal`, eux, sont dormants aujourd'hui (rien ne les lit côté envoi) —
    // mais le marquage vaut pour le plus engageant des trois.
    marquages: ["sortant"],
    autonome: false,
  },

  // ══ LE CONTENU PARTAGÉ ET LE SMS ═════════════════════════════════════════

  shared_fragments_save: {
    ecran: "messages",
    endpoint: "/api/beds24-mapping",
    action: "shared_fragments_save",
    // handleSharedFragmentsSave : upsert shared_message_fragments SUR `key`
    // (clé absente ⇒ CRÉATION, clé connue ⇒ REMPLACEMENT du corps). body_fr
    // obligatoire ; chaque corps passe la whitelist INTERSECTION des deux
    // moteurs (sinon le fragment rendrait différemment selon l'expéditeur) ;
    // `{{frag:}}` imbriqué refusé.
    //
    // ⚠️ UN FRAGMENT EST LU PAR LES DEUX MOTEURS À LA FOIS : le réécrire change
    // le texte de TOUS les messages qui le citent, Plateformes et Direct
    // confondus. Le `constatable` doit donc envoyer Dimitri regarder la liste,
    // pas un message en particulier.
    // ⚠️ `is_active: false` est la voie de retrait RÉVERSIBLE — c'est pour ça
    // que `shared_fragments_delete` (DELETE dur) est écarté, voir la note de fin.
    args_autorises: [
      "key",
      "name",
      "description",
      "body_fr",
      "body_en",
      "body_es",
      "is_active",
    ],
    titre: "Écrire ou couper un fragment partagé",
    constatable:
      "le fragment apparaît (ou passe en inactif) dans la liste du contenu partagé, avec son texte — que reprendront, au prochain envoi au voyageur, tous les messages qui le citent",
    sujet: "messages",
    cout: ["rien"],
    ou_ca_part: ["atteignable"],
    sans_retour:
      "le corps remplacé n'est gardé nulle part, et il était peut-être cité par plusieurs messages : ce qui est écrasé l'est pour tous",
    // ⚠️ LE RAYON D'ACTION EST LA CONFUSION : on croit toucher un texte, on
    // touche tous les messages qui le citent, des DEUX moteurs.
    ne_fait_pas:
      "il ne touche pas un message en particulier : un fragment est lu par les deux moteurs à la fois (Plateformes ET Direct), et le réécrire change le texte de TOUS ceux qui le citent",
    apercu_absent:
      "aucune lecture ne rend le corps actuel du fragment ni la LISTE des messages qui le citent : il faudrait `shared_fragment_lire`. C'est ici que l'aperçu manquerait le plus — le rayon d'action est invisible.",
    dit_comme: [
      "Change le bloc de texte qu'on réutilise partout",
      "Réécris la signature commune de mes messages",
      "Désactive ce morceau de texte partagé",
    ],
    vague: 3,
    motif_requis: false,
    marquages: ["sortant"],
    autonome: false,
  },

  sms_template_save: {
    ecran: "messages",
    endpoint: "/api/beds24-mapping",
    action: "sms_template_save",
    // handleSmsTemplateSave, op 'set' : upsert sms_templates {body_fr} + EN/ES
    // traduits SERVEUR (jamais lus du client, échec ⇒ NULL ⇒ fallback FR).
    // Slug restreint aux gabarits de SMS_TEMPLATE_RULES. Re-validation serveur
    // GSM-7 strict / budget 160 pire cas / whitelist de variables : la moindre
    // erreur = 400 et AUCUNE écriture.
    //
    // ⚠️ `op` absent de la liste pour la même raison que direct_messages_save :
    // `op:'reset'` efface le corps édité (retour au texte codé) sans retour.
    // `regenerate` est admis : il ne fait que retenter la traduction.
    args_autorises: ["slug", "body_fr", "regenerate"],
    // ⚠️ LE RÉSOLVEUR LIT LA LISTE DU CODE, PAS LA TABLE. Ce geste fait un
    // UPSERT sur un slug de `SMS_TEMPLATE_RULES` : un gabarit jamais édité n'a
    // AUCUNE ligne en base et reste pourtant visable. Lire `sms_templates`
    // ferait disparaître de la désignation exactement les gabarits qu'on n'a
    // pas encore touchés — en silence.
    args_resolus: {
      slug: { objet: "gabarit_sms", par: ["nom"] },
    },
    titre: "Réécrire le texte d'un gabarit SMS",
    constatable:
      "le gabarit affiche le nouveau texte dans Messages › SMS, avec le nombre de SMS qu'il coûtera au pire — et c'est CE texte qui partira quand le canal SMS s'ouvrira",
    sujet: "messages",
    cout: ["rien"],
    ou_ca_part: ["atteignable"],
    sans_retour:
      "le corps remplacé n'est gardé nulle part ; les traductions EN/ES sont refaites par le serveur et écrasent les précédentes",
    // ⚠️ L'ÉTAT ACTUEL, PAS UN PROJET (règle R5) : le canal SMS est INERTE.
    ne_fait_pas:
      "aucun SMS ne part aujourd'hui : l'envoi de SMS est fermé. Ce texte attend qu'on l'ouvre — il ne l'ouvre pas.",
    apercu_absent:
      "aucune lecture ne rend le texte actuel du gabarit ni le nombre de SMS qu'il coûte : il faudrait `sms_template_lire`. Le serveur re-vérifie les caractères autorisés et la longueur, et refuse en bloc — mais après le clic.",
    dit_comme: [
      "Réécris le texte de ce SMS",
      "Corrige le message court d'arrivée",
      "Ce SMS est trop long, raccourcis-le",
    ],
    vague: 3,
    motif_requis: false,
    marquages: ["sortant"],
    autonome: false,
  },

  sms_send_save: {
    ecran: "messages",
    endpoint: "/api/beds24-mapping",
    action: "sms_send_save",
    // handleSmsSendSave, op 'set' : écrit une ligne message_sends canal='sms'
    // + sa ligne `messages` dédiée. C'est le SEUL chemin d'écriture d'un send
    // SMS, et le plus lourd de la famille : il ne choisit pas des mots, il
    // choisit QUI reçoit (active_logement_ids, conditions) et QUAND
    // (trigger_*). Forcés serveur : canal='sms', destinataire_override=null,
    // plateforme `direct` refusée (ADR-266), garde caution × Airbnb.
    //
    // ⚠️ DEUX ARGUMENTS SONT VOLONTAIREMENT ABSENTS DE LA LISTE :
    //   • `op` — l'omettre force 'set'. `op:'delete'` supprime EN DUR le send
    //     ET sa ligne `messages` (donc le texte) : deux suppressions sèches
    //     derrière un mot qui n'en annonce qu'une.
    //   • `status` — l'omettre force le défaut serveur 'draft'. Une intention
    //     composée ici ne peut donc JAMAIS armer un envoi : elle prépare un
    //     brouillon, que Dimitri active ensuite à la main dans l'écran. Effet
    //     de bord assumé sur une ÉDITION : un send déjà actif retomberait en
    //     brouillon — ça referme, jamais ça n'ouvre, et le sens est le bon.
    args_autorises: [
      "send_id",
      "name",
      "body_fr",
      "trigger_type",
      "trigger_offset_value",
      "trigger_offset_unit",
      "trigger_time",
      "trigger_fixed_date",
      "conditions",
      "active_logement_ids",
      "catchup_mode",
    ],
    // Le tableau de conditions (plateformes visées) — sans cette déclaration
    // le socle refusait le tableau et le geste ne se composait jamais.
    args_formes: { conditions: { liste: true, max: 20 } },
    // ⚠️ `requis: []` SUR LE SEND, PARCE QUE CE LEVIER CRÉE AUSSI. Sans
    // `send_id`, le handler monte un envoi NEUF : exiger le repère refuserait
    // un geste légitime, et Dimitri lirait une panne. La plateforme, elle,
    // ne DÉSIGNE rien — elle départage deux envois homonymes (« celui de
    // Booking »), et son absence ne bloque jamais.
    args_resolus: {
      send_id: {
        objet: "envoi_sms",
        par: ["nom", "plateforme"],
        requis: [],
      },
    },
    titre: "Préparer un envoi SMS Plateformes",
    constatable:
      "l'envoi apparaît en brouillon dans Messages › Plateformes › SMS, avec son texte, son déclencheur et les logements ciblés — rien ne part tant que Dimitri ne l'a pas activé",
    // Texte neuf écrit par Dimitri ET ciblage d'un envoi : le cas le plus
    // engageant de la famille, donc la dernière vague.
    sujet: "messages",
    cout: ["rien"],
    ou_ca_part: ["atteignable"],
    sans_retour:
      "sur une édition, le send retombe en brouillon : s'il était actif, il faudra le réarmer à la main — le geste referme, il n'ouvre jamais",
    ne_fait_pas:
      "il n'arme RIEN : le statut est forcé « brouillon » côté serveur, et rien ne part tant que Dimitri ne l'active pas dans l'écran. Le canal SMS est par ailleurs fermé aujourd'hui.",
    apercu_absent:
      "aucune lecture ne rend l'envoi actuel (son texte, son déclencheur, les logements ciblés) : il faudrait `sms_send_lire`. Sur une édition, la carte ne montre donc ni ce qu'on remplace, ni qu'on va désarmer un envoi actif.",
    dit_comme: [
      "Prépare un SMS de rappel avant l'arrivée",
      "Monte un envoi SMS pour ces logements",
      "Prépare un message court à envoyer la veille",
    ],
    vague: 9,
    motif_requis: false,
    marquages: ["sortant"],
    autonome: false,
  },

  // ══ RÉPONDRE À UN VOYAGEUR — LE SEUL LEVIER QUI ENVOIE AU CLIC ═══════════
  //
  // Dimitri, 2026-09-13 : « oui, ouvre-le — mais avec ma validation à chaque
  // fois, jamais à la voix. »
  //
  // C'est l'exception à la ligne de partage écrite en tête de fichier : ici,
  // le clic FAIT PARTIR quelque chose (ou le simule, tant que `MESSAGES_DRY_RUN`
  // tient). Tout ce qui suit sert à ce que Dimitri le voie AVANT.
  //
  // ⚠️ « JAMAIS À LA VOIX » — OÙ LA RÈGLE VIT (vérifié dans le code) :
  //   · `autonome: false` + marquage `sortant` : `_forme.js` REFUSE un levier
  //     sortant autonome — même le jour où une vanne d'autonomie existera, ce
  //     geste n'y entrera pas ;
  //   · l'exécution est `src/lib/geste-executer.js`, appelée UNIQUEMENT par le
  //     `onClick` d'une carte (`CarteGesteDemande` / `CarteGeste`). La voix
  //     (`src/lib/conversation-vocale.js`, `poser`) ne sait qu'ENVOYER UNE
  //     QUESTION : elle n'a pas de chemin vers un clic ;
  //   · la garde `sollicite` / `enVocalRef` de `BlocOperateur.jsx` ne couvre
  //     QUE le déplacement d'écran (`emmener`) — elle n'est pas ce qui protège
  //     ici, et il ne faut pas croire qu'elle le fait.
  //
  // ⚠️ LA PORTE N'EST PAS LE GUICHET. `inbox_reply` est un `type` de
  // `api/send-email.js`, pas une action du routeur : `lib/inbox-reply-geste.js`
  // traduit l'enveloppe, refuse avant le guichet ce qui n'a pas de coordonnée,
  // et rend 409 quand le guichet répond 200 sans rien avoir envoyé (sinon la
  // carte dirait « fait » sur un `skipped`). Le guichet est octet-identique.
  //
  // ⚠️ TCHAT DE LA PLATEFORME SEULEMENT. Le canal `email` exige `toEmail`, le
  // SMS `guestPhone` : des coordonnées PII que le modèle devrait composer —
  // donc écrire dans le journal et sur la carte. RGPD : non. Le tchat OTA se
  // coordonne par `beds24BookingId`, un identifiant.
  //
  // ⚠️ LE CORPS EST L'ARGUMENT LIBRE, ET IL EST BORNÉ PAR LE SOCLE : même
  // patron que `menage_instruction_save.body` — listé, sans forme, donc plafonné
  // par `MAX_CARS_ARG` (120) dans `lib/operateur-geste.js`. Une réponse plus
  // longue n'est pas tronquée, elle n'est PAS COMPOSÉE (aucune carte). C'est
  // fail-closed, pas un mensonge — mais c'est court, et la carte n'a pas de
  // champ pour que Dimitri RÉÉCRIVE le corps avant d'envoyer (seul `motif` est
  // éditable). Deux points à arbitrer, notés dans le rapport du 2026-09-13 —
  // on ne les résout pas ici en élargissant le socle en douce.
  inbox_reply: {
    ecran: "messages",
    endpoint: "/api/beds24-mapping",
    action: "inbox_reply",
    args_autorises: [
      "threadId",
      "canal",
      "body",
      "subject",
      "reservationId",
      "pricingBlockId",
      "logementId",
      "beds24BookingId",
      "inReplyToMessageId",
    ],
    // ⚠️ LE CORPS EST UN TEXTE, PAS UN ARGUMENT. Déclaré ici, il va jusqu'à
    // 4 000 caractères (`args_textes`, socle du 2026-09-13) au lieu des 120
    // qui bornent un identifiant. Sans cette ligne, un message de voyageur de
    // quatre lignes n'était PAS composé — et la carte ne disait pas pourquoi.
    args_textes: ["body"],
    // Recopié de `lib/inbox-reply-geste.js::CANAL_AUTORISE` — la porte refuse
    // tout autre canal avant le guichet ; on le dit au modèle plutôt que de le
    // laisser composer « email » et échouer sous le clic.
    args_valeurs: {
      canal: ["tchat_ota"],
    },
    titre: "Répondre à un voyageur sur le tchat",
    constatable:
      "la réponse apparaît dans le fil du voyageur, dans Messages › Inbox, et le fil passe en « répondu » — marquée simulée tant que l'envoi réel est fermé, partie chez le voyageur sinon",
    // Le texte est celui que Dimitri valide, et il va chez un tiers : la
    // dernière vague, sans discussion.
    sujet: "messages",
    cout: ["envoi"],
    ou_ca_part: ["voyageur"],
    sans_retour:
      "un message envoyé sur le tchat d'une plateforme ne se rappelle pas et ne s'édite pas : ce qui est parti est parti",
    // ⚠️ LE GATE SE CITE PAR SENDER, JAMAIS « EN SIMULATION RIEN NE PART » EN
    // GÉNÉRAL. C'est la leçon d'ADR-272 : le MÊME drapeau coupe cet envoi-ci
    // et NE COUPE PAS les e-mails transactionnels du parcours Direct. Une
    // carte qui généraliserait serait fausse pour la moitié des envois.
    ne_fait_pas:
      "quand l'app est en mode simulation, CE message-là ne part pas (il est marqué « simulé », personne ne le reçoit) — mais les e-mails des réservations directes, eux, partent quand même. Et ça passe par la messagerie de la plateforme : ni e-mail, ni SMS.",
    apercu: {
      lecture: "inbox_fil_lire",
      depend_de: ["threadId"],
      // ⚠️ `x-apercu-version`, PAS `x-cfg-version` — ce geste n'écrit pas le
      // blob de configuration. Le jeton prouve QUE LA CARTE A LU (executerGeste
      // refuse de partir sans lui) ; l'écriture, elle, ne le vérifie pas encore
      // (lot à part, avec l'écran — cf. lib/apercus-messages-handlers.js).
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Réponds à ce voyageur",
      "Dis-lui que le code arrive demain",
      "Envoie-lui un mot pour le rassurer",
    ],
    vague: 9,
    motif_requis: false,
    marquages: ["sortant"],
    // ⚠️ EXPLICITE, même si `_forme.js` l'imposerait : « jamais à la voix,
    // avec ma validation à chaque fois » se lit ici, pas dans une règle
    // dérivée trois fichiers plus loin.
    autonome: false,
  },

  // ── COUPER L'ENVOI RÉEL — LE GESTE QUI NE VA QUE DANS UN SENS ────────────
  // Dimitri, 2026-09-09 : « fais la petite action dédiée pour couper l'envoi
  // réel sur un logement. Fermer est inoffensif, ouvrir ne doit pas l'être —
  // deux gestes séparés, pas un seul qui fait les deux sens. »
  //
  // ⚠️ `inbox_real_send_config_save` reste HORS du registre (E5 : l'opérateur
  // ne propose pas de lever la barrière qui le tient). Le levier ci-dessous
  // pointe une action DIFFÉRENTE, qui ne sait qu'éteindre — il n'y a donc rien
  // à filtrer côté valeurs, ce que l'allow-list ne saurait pas faire.
  //
  // ⚠️ Il ne touche PAS `ia_enabled` : les deux verrous restent distincts. Le
  // copilote reste éligible et devient muet — l'état recherché quand on veut
  // l'arrêter sans le démonter.
  inbox_real_send_couper: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "inbox_real_send_couper",
    args_autorises: ["logement_id"],
    args_resolus: { logement_id: { objet: "logement", par: ["logement"] } },
    titre: "Couper l'envoi réel du copilote",
    constatable:
      "l'interrupteur « envoi réel » du logement retombe sur éteint dans sa fiche — le copilote continue de préparer, plus rien ne part au voyageur",
    // Vague 1 : réversible en un geste (l'interrupteur d'origine rallume),
    // rien ne sort, constatable sur-le-champ, zéro argent.
    sujet: "messages",
    onglet: "savoir",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour: null, // l'interrupteur d'origine rallume, dans la fiche
    // ⚠️ L'ASYMÉTRIE EST LE GARDE-FOU : ce geste ne sait que FERMER. Ouvrir
    // est E5 (l'opérateur ne lève pas ce qui le tient).
    ne_fait_pas:
      "il ne coupe pas le copilote : celui-ci continue de préparer des brouillons. Et il ne sait pas rallumer — l'ouverture de la vanne reste la main de Dimitri, dans la fiche.",
    apercu_absent:
      "aucune lecture ne dit si l'envoi réel de ce logement est allumé : il faudrait `inbox_real_send_lire`. Couper ce qui est déjà coupé est sans dommage, mais la carte devrait le dire.",
    dit_comme: [
      "Coupe l'envoi automatique sur ce logement",
      "Arrête que l'assistant envoie tout seul au Cocon",
      "Ne laisse plus partir ses réponses sans moi",
    ],
    vague: 1,
    motif_requis: false,
    // ⚠️ `autonome: false` MALGRÉ les quatre questions satisfaites : le geste
    // est sûr, mais aucun fait ne dirait QUAND le poser. Un opérateur qui
    // couperait son propre envoi de sa propre initiative serait exactement le
    // genre d'initiative qu'on ne saurait pas expliquer après coup.
    autonome: false,
  },
});

// ══ CE QUI N'ENTRE PAS, ET POURQUOI (sept actions du périmètre) ═════════════
//
// • inbox_ia_escalation_resolve — DÉJÀ déclaré dans `leviers/operateur.js`. Un
//   doublon lèverait à l'agrégation ; c'est voulu, on ne le redéclare pas ici.
//
// • inbox_real_send_config_save — E5, nommément. L'exclusion cite le cas mot
//   pour mot : « rien qui laisse l'opérateur agir SUR LUI-MÊME (…) ouvrir sa
//   vanne d'envoi réel ». `real_send_enabled` est la 2e barrière du copilote :
//   la lever, c'est l'opérateur qui propose de retirer ce qui le tient. Ce
//   n'est pas une question de risque technique — le handler est propre, il
//   exige déjà un jeton `confirm:'ENVOI_REEL'` et se limite à lc1/lc2/lc3 —
//   c'est une question de qui décide. Dimitri clique cet interrupteur-là
//   lui-même, depuis la fiche du logement, sans qu'on le lui ait suggéré.
//   ⚠️ Et l'asymétrie compte : REFERMER la vanne (`real_send_enabled:false`)
//   serait inoffensif, mais un seul levier ne sait pas faire une seule des deux
//   directions — `args_autorises` filtre les CLÉS, jamais les VALEURS. Ouvrir
//   et fermer voyagent ensemble ; on garde donc les deux dehors.
//
// • inbox_ia_faq_delete / shared_fragments_delete / direct_messages_delete —
//   E1, les trois pour la même raison : DELETE DUR, rien ne revient. Et chacun
//   a déjà, dans cette même famille, sa version RÉVERSIBLE, qui est déclarée :
//     FAQ        → `inbox_ia_faq_update` avec `status:'archived'` ;
//     fragment   → `shared_fragments_save` avec `is_active:false` ;
//     msg manuel → `direct_messages_settings` avec `status:'brouillon'`.
//   Retirer du service sans effacer fait le travail demandé dans tous les cas
//   où Dimitri dit « enlève-moi ça ». Le vrai effacement reste un geste de la
//   main, dans l'écran, devant le texte qu'on perd.
//   ⚠️ `shared_fragments_delete` mérite une ligne de plus : un corps qui cite
//   encore `{{frag:KEY}}` ne casse pas là où on supprime, mais AILLEURS, au
//   prochain envoi (skip côté OTA, repli builder côté Direct). L'effet déborde
//   ce que le libellé annonce — le motif exact des INTERDITS_ABSOLUS.
//
// • sms_send_save `op:'delete'` — même E1, traité par l'omission de `op` plutôt
//   que par le retrait du levier (cf. son commentaire).

// ══ LES BOOLÉENS DE CETTE FAMILLE, EN MOTS — `levier.argument` ════════════
//
// F5 (03/10/2026) : déplacées telles quelles de `lib/operateur-leviers.js`,
// qui les rassemble (la règle et ses gardes sont écrites là-bas, au-dessus de
// `NOMS_BOOLEENS`). Une phrase se déclare ICI, à côté de son levier, et nulle
// part ailleurs.
const BOOLEENS_LIBELLES = Object.freeze({
  "shared_fragments_save.is_active": {
    oui: "Garder le fragment en service",
    non: "Couper le fragment",
  },
  "sms_template_save.regenerate": {
    oui: "Régénérer le texte",
    non: "Garder le texte tel quel",
  },
  "sms_send_save.catchup_mode": {
    oui: "Rattraper les envois passés",
    non: "Ne pas rattraper les envois passés",
  },
});

module.exports = { LEVIERS_MESSAGES, BOOLEENS_LIBELLES };
